import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { useRefreshOnResume } from "@/hooks/useRefreshOnResume";
import { track } from "@/integrations/posthog/client";
import { openExternalUrl } from "@/utils/linkify";
import { nativeCampaignCta } from "@/lib/externalLinkPolicy";
import { fetchIapEnabled } from "@/hooks/useIapEnabled";
import { IN_APP_PAYWALL_PATH } from "@/lib/inAppSubscribe";
import { fetchActiveBanners, type PopupCampaign } from "@/services/popupService";
import BannerStrip from "@/components/banners/BannerStrip";

const DISMISS_KEY = "dismissedBanners";
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24h — see plan Global Constraints

// BannerHost lives in <Layout>, which several pages (/fasting, /steps,
// /water-tracking, and every non-Group-A route) re-mount on navigation, resetting
// component state. Track already-counted impressions at module scope so one banner
// logs at most one banner_shown per app session, regardless of remounts.
const shownThisSession = new Set<string>();

function readDismissed(): Record<string, number> {
  try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || "{}"); } catch { return {}; }
}
function isDismissed(id: string): boolean {
  const at = readDismissed()[id];
  return typeof at === "number" && Date.now() - at < DISMISS_TTL_MS;
}
function markDismissed(id: string): void {
  try {
    const all = readDismissed();
    all[id] = Date.now();
    localStorage.setItem(DISMISS_KEY, JSON.stringify(all));
  } catch { /* private mode / quota — dismissal just won't persist */ }
}

/** Fetches audience-matched banner campaigns and renders the highest-priority one
 * not dismissed within 24h. A failed fetch is non-fatal. Server RPC already scopes
 * the audience, so there is no client-side targeting here. */
const BannerHost = () => {
  const navigate = useNavigate();
  const [banners, setBanners] = useState<PopupCampaign[]>([]);
  const iapEnabledRef = useRef(false);
  const [dismissTick, setDismissTick] = useState(0); // re-evaluate after a dismiss

  const load = useCallback(async () => {
    try {
      // Drop checkout-bound campaigns on native before they can be picked — an
      // admin can point a banner CTA anywhere, and a native tap that reaches our
      // web checkout is an App Store 3.1.1 rejection (@/lib/externalLinkPolicy).
      // Filtering here rather than hiding the CTA keeps the next-priority banner
      // eligible instead of surfacing a strip whose tap does nothing.
      // Since IAP: when store purchases are live, a checkout-bound campaign still
      // shows and its tap goes to the in-app paywall instead (see handleCta).
      const isNative = Capacitor.isNativePlatform();
      const [fetched, iapEnabled] = await Promise.all([fetchActiveBanners(), fetchIapEnabled()]);
      iapEnabledRef.current = iapEnabled;
      setBanners(fetched.filter((b) => nativeCampaignCta(b, isNative, iapEnabled) !== "hidden"));
    }
    catch { /* non-fatal: a failed banner fetch must never block the app */ }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useRefreshOnResume(() => { void load(); });

  // RPC already priority-orders; pick the first not dismissed. dismissTick forces
  // a recompute when the user dismisses the current one.
  const active = banners.find((b) => !isDismissed(b.id)) ?? null;
  void dismissTick;

  useEffect(() => {
    if (active && !shownThisSession.has(active.id)) {
      shownThisSession.add(active.id);
      track("banner_shown", { campaign_id: active.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id]);

  if (!active) return null;

  const handleDismiss = () => {
    track("banner_dismissed", { campaign_id: active.id });
    markDismissed(active.id);
    setDismissTick((t) => t + 1);
  };

  const handleCta = () => {
    track("banner_cta_clicked", { campaign_id: active.id });
    const { cta_action_type, cta_value } = active;
    if (!cta_value) return;
    if (nativeCampaignCta(active, Capacitor.isNativePlatform(), iapEnabledRef.current) === "paywall") {
      navigate(IN_APP_PAYWALL_PATH);
    } else if (cta_action_type === "external_url") void openExternalUrl(cta_value);
    else navigate(cta_value);
  };

  return <BannerStrip campaign={active} onCtaClick={handleCta} onDismiss={handleDismiss} />;
};

export default BannerHost;
